import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import type { ScriptStatusDto } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { Button } from '../../shared/ui/Button.js';

const COPIED_FEEDBACK_MS = 1600;
const BYTES_PER_KB = 1024;

/**
 * The single line the site adds to its template (functional §6.2, technical
 * §16.3). The integrity pins the exact file: a modified byte is refused by the
 * browser. A new SDK build therefore means a new tag, handed over the same way.
 */
export function IntegrationTag({ status }: { status: ScriptStatusDto }) {
  const [copied, setCopied] = useState(false);

  if (!status.sdk || !status.tag) {
    return (
      <p className="script__hint" role="status">
        Le script d'aide n'est pas encore construit sur la plateforme : lancez <code>npm run sdk:budget</code>
        , puis rechargez la page.
      </p>
    );
  }

  const copy = () => {
    void navigator.clipboard.writeText(status.tag ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <>
      <div className="script__meta">
        <Badge tone="brand">Script d'aide v{status.sdk.version}</Badge>
        <Badge>
          {(status.sdk.gzipBytes / BYTES_PER_KB).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Ko
          compressé
        </Badge>
        <span title="Le navigateur vérifie que le fichier chargé est exactement celui publié, au octet près.">
          <Badge tone="teal">Empreinte de sécurité</Badge>
        </span>
        <span className="script__meta-spacer" />
        <Button
          icon={copied ? <Check size={13} /> : <Copy size={13} />}
          onClick={copy}
          aria-label="Copier la balise"
        >
          {copied ? 'Copiée' : 'Copier la balise'}
        </Button>
      </div>
      <pre className="script__code" data-testid="integration-tag">
        <code>{status.tag}</code>
      </pre>
      <p className="script__hint">
        À poser une seule fois dans le modèle commun des pages du portail. Elle ne ralentit jamais l'affichage
        du site. Retirer la ligne arrête tout ; l'interrupteur ci-dessus fait de même sans toucher au site. En
        démonstration, l'Extension Ederest charge exactement le même fichier.
      </p>
    </>
  );
}
